export const jmdsProposal = {
  meta: {
    title: "JMD Website Proposal",
    clientName: "John Martin Drafting",
    siteDomain: "jmds.au",
    url: "https://jmds.au",
    date: "2025-12-09",
    quoteNumber: "Q-2025-JMDS",
    preparedBy: "Jason Toomer",
    summary: [
      "Hi John, thanks again for the discussion about your current portal and website needs.",
      "This proposal focuses on a clean, modern brochure site for jmds.au that clearly explains your drafting services.",
    ],
  },
  // rest of your existing JMD proposal object

  hero: {
    eyebrow: "Website + ecommerce proposal",
    heading: "A high-performing Shopify store for ESPpresso",
    intro:
      "A Shopify-based ecommerce website with subscriptions, blog, and a modern, image-led design built for conversions and long-term growth.",
    contact: {
      agencyName: "2merative Web Design",
      contactName: "Jason Toomer",
      email: "hello@2merative.com",
      phone: "+61 400 000 000",
    },
  },

  goals: [
    "Launch a reliable Shopify store for ESPpresso with a strong visual brand presence.",
    "Enable retail sales with a streamlined cart and checkout experience.",
    "Add subscription capability for coffee plans with customer self-management controls.",
    "Support organic growth with a blog and SEO-ready structure.",
    "Deliver a fast, mobile-first experience with strong Core Web Vitals outcomes.",
  ],

  sections: [
    {
      id: "overview",
      title: "Project overview",
      paragraphs: [
        "ESPpresso.com will be built on Shopify to provide a stable, fully managed ecommerce backend with a modern, conversion-focused front-end experience.",
        "The site will be designed to be highly visual and image-rich, with clear CTAs, trust signals, and a straightforward purchase path for both one-time and subscription orders.",
      ],
    },

    {
      id: "platform",
      title: "Platform",
      bullets: [
        "Shopify hosted platform with SSL and managed checkout.",
        "Theme build and configuration aligned to the ESPpresso brand.",
        "Scalable catalogue foundation for 30+ products.",
      ],
    },

    {
      id: "scope",
      title: "Scope of work",
      bullets: [
        "Design and build a Shopify website aligned to the ESPpresso brand.",
        "Pages: Home, About, Blog, Products, Contact, and essential policy pages (Privacy, Shipping, Returns).",
        "Product catalogue structure with categories, tags, and search/filter readiness.",
        "Subscription setup for coffee plans, including customer portal access and pause/cancel controls.",
        "Conversion UX: trust badges, testimonials/reviews sections (where applicable), clear pricing blocks, and checkout flow optimisation.",
        "Performance tuning: lazy loading, image optimisation, and mobile-first layout refinement.",
        "Accessibility best practice aligned to WCAG 2.1 AA where practical for a Shopify storefront.",
        "Advanced SEO setup: metadata templates, clean URLs, XML sitemap, robots rules, and structured data (Product + BlogPosting).",
        "Forms protection for contact/subscription capture using reCAPTCHA or hCaptcha (where applicable).",
        "Security and reliability workflow guidance: backups, update approach, and stable deployment practices.",
      ],
    },

    {
      id: "inputs",
      title: "What the owner provides",
      paragraphs: [
        "To keep the build efficient and accurate, the owner supplies the core business content and product inputs. We optimise and implement what you provide.",
      ],
      bullets: [
        "Product list, pricing, variants, and subscription plan structure (if applicable).",
        "Product images and brand imagery (we will optimise for speed and quality).",
        "Brand references or preferences (look and feel, competitors you like, do-not-like notes).",
        "Core site copy, or approval to draft copy based on supplied notes and structure.",
      ],
    },

    {
      id: "contentProduction",
      title: "Content creation note",
      paragraphs: [
        "If you would like me to create content (copywriting, graphics, product photography coordination, reels, or video editing) this is additional to the build scope and will be quoted separately based on requirements.",
      ],
    },

    {
      id: "extras",
      title: "Additional inclusions",
      bullets: [
        "Landing page build at coffeejourney.com.au (free of charge).",
        "DNS and deployment support as required for go-live.",
      ],
    },

    {
      id: "social",
      title: "Instagram management",
      paragraphs: [
        "Optional Instagram management can be included for both brands. Posting assumes the owner supplies approved content (images/video/copy prompts) on time.",
      ],
      bullets: [
        "Coffee Journey Instagram: 2–3 posts per week (owner provides content).",
        "ESPpresso Instagram: 2–3 posts per week (owner provides content).",
        "If content must be produced by me, this is an additional charge and will be quoted separately.",
      ],
    },

    {
      id: "hosting",
      title: "Hosting and email",
      bullets: [
        "Shopify hosting + SSL included with Shopify plan.",
        "1 business email address via GoDaddy or Hostinger (as required).",
        "DNS support included.",
        "Email hosting cost: $240/year (including email).",
      ],
    },

    {
      id: "updates",
      title: "Ongoing updates and support",
      bullets: [
        "Includes up to 2 hours per month for product/content/blog updates.",
        "Additional time billed at $100/hour.",
      ],
    },
  ],

  pricing: {
    currency: "AUD",
    disclaimer:
      "All pricing below is indicative. Final pricing will be confirmed in writing once scope and inclusions are locked.",
    items: [
      {
        label: "Shopify website build (ESPpresso.com)",
        description:
          "Custom Shopify build including core pages, catalogue structure, subscriptions setup, performance tuning, SEO foundations, and conversion-focused UX.",
        amount: 0,
      },
      {
        label: "ESPpresso logo design",
        description:
          "Logo design and development for ESPpresso, delivered in web and print-ready formats.",
        amount: 0,
      },
      {
        label: "Email hosting (1 mailbox)",
        description:
          "1 business email address via GoDaddy or Hostinger. Includes DNS support as required.",
        amount: 240,
        recurring: "yearly",
      },
      {
        label: "Included monthly updates",
        description:
          "Up to 2 hours per month for product/content/blog updates. Additional time billed at $100/hour.",
        amount: 0,
        recurring: "monthly",
      },
      {
        label: "Instagram management (optional add-on)",
        description:
          "2–3 posts per week for Coffee Journey and 2–3 posts per week for ESPpresso. Owner supplies content. If content must be produced by me, an additional fee applies.",
        amount: 0,
        optional: true,
      },
      {
        label: "CoffeeJourney.com.au landing page",
        description: "Landing page build provided free of charge.",
        amount: 0,
      },
    ],
    showTotals: false,
    paymentSchedule: [
      {
        label: "Project commencement",
        detail: "Due on acceptance of proposal.",
        percentage: 50,
      },
      {
        label: "Pre-launch milestone",
        detail: "Due when the site is built and ready for final review and go-live.",
        percentage: 50,
      },
    ],
  },

  maintenancePlans: [
    {
      name: "Included monthly updates",
      priceLabel: "Up to 2 hours/month included",
      tagline: "Ongoing product, content, and blog updates.",
      includes: [
        "Up to 2 hours of updates per month.",
        "Additional time billed at A$100/hour.",
        "Ideal for steady catalogue and content maintenance.",
      ],
    },
  ],

  faqs: [
    {
      question: "Is Shopify confirmed for this build?",
      answer:
        "Yes. This proposal assumes Shopify as the ecommerce platform and includes configuration of products, checkout, and subscriptions within Shopify.",
    },
    {
      question: "Do you provide product photos and copywriting?",
      answer:
        "You provide the product images and core product details. If you want me to produce content (copywriting, graphics, or media), that is an additional cost and will be quoted separately based on scope.",
    },
    {
      question: "Can the store scale beyond 30 products?",
      answer:
        "Yes. The catalogue will be structured to support growth using categories, tags, and search/filter readiness.",
    },
    {
      question: "What is included in the monthly updates?",
      answer:
        "Up to 2 hours per month for product, content, and blog updates. Extra time is billed at A$100/hour.",
    },
  ],

  acceptance: {
    heading: "Next steps",
    blurb:
      "If this scope feels right, confirm acceptance and I will finalise pricing, lock the delivery timeline, and start the build.",
    terms: [
      "You confirm you are authorised to approve this work on behalf of ESPpresso.",
      "Final pricing and inclusions will be confirmed in writing before work begins.",
      "Deposit is due on acceptance. Final payment is due at the pre-launch milestone.",
      "Instagram management is optional and priced separately if content creation is required.",
      "Build speed depends on timely supply of product details, pricing, images, and brand inputs.",
    ],
  },
}
